'use client'

import { AlertCircle } from 'lucide-react'
import { useChunkRecovery } from '@/lib/use-chunk-recovery'

/**
 * Last-resort boundary for render errors that escape every nested error.tsx,
 * including failures in the root layout. It replaces the whole document, so it
 * has to ship its own <html>/<body> and can't rely on Providers or global CSS
 * being mounted — styling stays inline.
 *
 * posthog-js `capture_exceptions` catches window.onerror/unhandledrejection,
 * but React swallows render errors before they reach those handlers, so this
 * reports explicitly (via useChunkRecovery, which also reloads once for a
 * failed chunk since re-rendering can't fix that).
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  if (useChunkRecovery(error)) {
    return (
      <html lang="en">
        <body style={{ margin: 0, backgroundColor: '#0A0A0A' }} />
      </html>
    )
  }

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          backgroundColor: '#0A0A0A',
          color: '#fff',
          fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: '28rem',
            width: '100%',
            borderRadius: '1rem',
            border: '1px solid rgba(255,255,255,0.1)',
            backgroundColor: 'rgba(255,255,255,0.03)',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <AlertCircle
            style={{ width: 40, height: 40, margin: '0 auto 1rem', color: '#ef4444' }}
          />
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
            Something went wrong
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 1.5rem' }}>
            We&apos;ve been notified and are looking into it.
          </p>
          <button
            type="button"
            onClick={reset}
            style={{
              borderRadius: '0.5rem',
              border: 'none',
              backgroundColor: '#ef4444',
              color: '#fff',
              padding: '0.625rem 1.25rem',
              fontSize: '0.875rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </body>
    </html>
  )
}
